import { useEffect, useState } from 'react'
import { SocialListeningApi } from '@/lib/api'
import { useSocialListeningApi } from './SocialListeningApiContext'

/** Object URLs kept for images no component shows any more; older ones are revoked. */
export const SOCIAL_IMAGE_CACHE_MAX_IDLE = 200

interface SocialImageCacheEntry {
  promise: Promise<string>
  objectUrl: string | null
  /** Mounted components showing this image; an entry in use is never revoked. */
  users: number
  lastUsedAt: number
}

const imageCache = new Map<string, SocialImageCacheEntry>()
let useClock = 0

function evictIdleImages() {
  const idle = [...imageCache.entries()].filter(([, entry]) => entry.users === 0 && entry.objectUrl)
  if (idle.length <= SOCIAL_IMAGE_CACHE_MAX_IDLE) {
    return
  }
  idle.sort((a, b) => a[1].lastUsedAt - b[1].lastUsedAt)
  for (const [url, entry] of idle.slice(0, idle.length - SOCIAL_IMAGE_CACHE_MAX_IDLE)) {
    URL.revokeObjectURL(entry.objectUrl as string)
    imageCache.delete(url)
  }
}

function acquireImage(api: SocialListeningApi, imagePath: string): { url: string; entry: SocialImageCacheEntry } {
  const url = api.imageUrl(imagePath)
  let entry = imageCache.get(url)
  if (!entry) {
    const created: SocialImageCacheEntry = {
      objectUrl: null,
      users: 0,
      lastUsedAt: 0,
      promise: api.fetchItemImage(imagePath).then((blob) => {
        const objectUrl = URL.createObjectURL(blob)
        created.objectUrl = objectUrl
        return objectUrl
      }),
    }
    // A failed load is forgotten, so the next mount tries again.
    created.promise.catch(() => {
      if (imageCache.get(url) === created) {
        imageCache.delete(url)
      }
    })
    imageCache.set(url, created)
    entry = created
  }
  entry.users += 1
  entry.lastUsedAt = ++useClock
  return { url, entry }
}

function releaseImage(entry: SocialImageCacheEntry) {
  entry.users = Math.max(0, entry.users - 1)
  entry.lastUsedAt = ++useClock
  evictIdleImages()
}

/** Revokes every cached object URL (tests, or after the viewer changes org). */
export function clearSocialImageCache() {
  for (const entry of imageCache.values()) {
    if (entry.objectUrl) {
      URL.revokeObjectURL(entry.objectUrl)
    }
  }
  imageCache.clear()
}

interface UseSocialItemImageResult {
  /** Object URL for `<img src>`; null while loading, on error, or without a path. */
  src: string | null
  isLoading: boolean
  hasError: boolean
}

/**
 * A stored post image through the authenticated byte proxy. Images need the
 * session cookie (and the back office its admin base), so they are fetched as
 * blobs and shown from object URLs, cached per URL across tiles, the drawer and
 * post detail. `imagePath` is a `SocialPostImage.imagePath` or a Topic's
 * `previewImagePaths` entry.
 */
export function useSocialItemImage(imagePath: string | null | undefined): UseSocialItemImageResult {
  const socialListeningApi = useSocialListeningApi()
  const [state, setState] = useState<{ key: string | null; src: string | null; hasError: boolean }>({
    key: null,
    src: null,
    hasError: false,
  })

  useEffect(() => {
    if (!imagePath) {
      setState({ key: null, src: null, hasError: false })
      return
    }
    let isActive = true
    const { url, entry } = acquireImage(socialListeningApi, imagePath)
    setState({ key: url, src: entry.objectUrl, hasError: false })
    entry.promise
      .then((objectUrl) => {
        if (isActive) {
          setState({ key: url, src: objectUrl, hasError: false })
        }
      })
      .catch(() => {
        if (isActive) {
          setState({ key: url, src: null, hasError: true })
        }
      })
    return () => {
      isActive = false
      releaseImage(entry)
    }
  }, [socialListeningApi, imagePath])

  const currentKey = imagePath ? socialListeningApi.imageUrl(imagePath) : null
  const isCurrent = state.key === currentKey
  const src = isCurrent ? state.src : null
  const hasError = isCurrent && state.hasError

  return {
    src,
    isLoading: Boolean(imagePath) && !src && !hasError,
    hasError,
  }
}
